import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import declareImageOverlay from './ImageOverlay';
import { Button } from 'react-bootstrap';
import './Map.css';

export type MapData = {
	imageUrl: string;
	bottomLeftLat: number;
	bottomLeftLon: number;
	topRightLat: number;
	topRightLon: number;
	heading: number;
};

export default function MapView() {
	const navigation = useNavigate();
	const [mapData, setMapData] = useState<MapData | undefined>(undefined);
	const [center, setCenter] = useState<{ lat: number, lng: number } | google.maps.LatLng>({ lat: 55.94838644928657, lng: 8.788491217743918 });
	const [delta, setDelta] = useState(0.0001);
	const [overlay, setOverlay] = useState<google.maps.OverlayView | null | any>(null);
	const { isLoaded, loadError } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: "AIzaSyC1Ey2MbBUXS3h_SZi0t25doN5gjxngO9A"
	});

	const [map, setMap] = useState<google.maps.Map | null>(null);

	useEffect(() => {
		if (isLoaded) {
			const USGSOverlay = declareImageOverlay();

			const bounds = new google.maps.LatLngBounds(
				new google.maps.LatLng(55.946136837554796, 8.782930212900958),
				new google.maps.LatLng(55.95063606101836, 8.794052222586878),
			);

			const overlay = new USGSOverlay(bounds, "https://aa-festival.dk/wp-content/uploads/2024/05/kort.jpg", 95, 0.5);
			overlay.setMap(map);
			setOverlay(overlay);
		}
	}, [isLoaded, map]);

	useEffect(() => {
		if (mapData === undefined) {
			return;
		}

		if (overlay === null) {
			return;
		}

		console.log("Setting map data", mapData);

		const bounds = new google.maps.LatLngBounds(
			new google.maps.LatLng(mapData.bottomLeftLat, mapData.bottomLeftLon),
			new google.maps.LatLng(mapData.topRightLat, mapData.topRightLon),
		);

		overlay.bounds_ = bounds;
		overlay.updateImage(mapData.imageUrl);
		overlay.rotation = mapData.heading;
		overlay.opacity = 0.5;
	}, [mapData, overlay, map]);

	const onLoad = useCallback(function callback(map: google.maps.Map) {
		setMap(map);
	}, []);

	const onUnmount = useCallback(function callback(map: google.maps.Map) {
		setMap(null)
	}, []);

	async function fetchMap() {
		const res = await fetch(`${process.env.REACT_APP_API_URL}/map`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		const data: MapData = await res.json();

		if (!res.ok) {
			console.error(data);
			return;
		}

		console.log(data);

		setCenter({ lat: (data.topRightLat + data.bottomLeftLat) * 0.5, lng: (data.topRightLon + data.bottomLeftLon) * 0.5 });
		if (map !== null) {
			map.setCenter({ lat: (data.topRightLat + data.bottomLeftLat) * 0.5, lng: (data.topRightLon + data.bottomLeftLon) * 0.5 });
			console.log("Setting center", { lat: (data.topRightLat + data.bottomLeftLat) * 0.5, lng: (data.topRightLon + data.bottomLeftLon) * 0.5 });
		}

		setMapData(data);
	}

	async function saveMapData() {
		if (mapData === undefined) {
			return;
		}

		const res = await fetch(`${process.env.REACT_APP_API_URL}/map`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(mapData),
		});

		const data: MapData = await res.json();

		if (!res.ok) {
			console.error(data);
			return;
		}
	}

	async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
		if (mapData === undefined) {
			return;
		}

		const file = e.target.files?.[0];
		if (!file) return;
		console.log(file);

		const res = await fetch(`${process.env.REACT_APP_API_URL}/presignedUrl`);
		const data = await res.json();

		const signedUrl = data.signedUrl;
		const imageUrl: string = data.imageUrl;

		await fetch(signedUrl, {
			method: 'PUT',
			body: file,
		});

		setMapData({ ...mapData, imageUrl });
	}

	useEffect(() => {
		fetchMap();
	}, []);

	if (!isLoaded || mapData === undefined) {
		return <div>Loading...</div>
	}

	// const c = map?.getCenter() || { lat: 0, lng: 0 };

	// setCenter(c);

	return (
		<div className="Map">
			<GoogleMap
				mapContainerStyle={containerStyle}
				zoom={16}
				onLoad={onLoad}
				onUnmount={onUnmount}
				onDragEnd={() => { setCenter(map?.getCenter() || { lat: 0, lng: 0 }) }}
				// options={{ center, mapId: "37410bd63977c700", mapTypeId: "hybrid" }}
				options={{ center, mapTypeId: "hybrid" }}
			/>

			<div className="buttons">
				<Button className="btn-success" style={{ gridArea: "1 / 2 / 2 / 3" }} onClick={() => { setMapData({ ...mapData, bottomLeftLat: mapData.bottomLeftLat + delta, topRightLat: mapData.topRightLat + delta }) }}>↑</Button>
				<Button className="btn-success" style={{ gridArea: "3 / 2 / 4 / 3" }} onClick={() => { setMapData({ ...mapData, bottomLeftLat: mapData.bottomLeftLat - delta, topRightLat: mapData.topRightLat - delta }) }}>↓</Button>
				<Button className="btn-success" style={{ gridArea: "2 / 1 / 3 / 2" }} onClick={() => { setMapData({ ...mapData, bottomLeftLon: mapData.bottomLeftLon - delta, topRightLon: mapData.topRightLon - delta }) }}>←</Button>
				<Button className="btn-success" style={{ gridArea: "2 / 3 / 3 / 4" }} onClick={() => { setMapData({ ...mapData, bottomLeftLon: mapData.bottomLeftLon + delta, topRightLon: mapData.topRightLon + delta }) }}>→</Button>

				<Button className="" style={{ gridArea: "1 / 4 / 2 / 5" }} onClick={() => { setMapData({ ...mapData, bottomLeftLon: mapData.bottomLeftLon + delta, topRightLon: mapData.topRightLon - delta }) }}>→←</Button>
				<Button className="" style={{ gridArea: "1 / 5 / 2 / 6" }} onClick={() => { setMapData({ ...mapData, bottomLeftLon: mapData.bottomLeftLon - delta, topRightLon: mapData.topRightLon + delta }) }}>↔</Button>
				<Button className="" style={{ gridArea: "2 / 4 / 3 / 5" }} onClick={() => { setMapData({ ...mapData, bottomLeftLat: mapData.bottomLeftLat - delta, topRightLat: mapData.topRightLat + delta }) }}>🡙</Button>
				<Button className="" style={{ gridArea: "2 / 5 / 3 / 6" }} onClick={() => { setMapData({ ...mapData, bottomLeftLat: mapData.bottomLeftLat + delta, topRightLat: mapData.topRightLat - delta }) }}>⇅</Button>

				<Button className="btn-warning" style={{ gridArea: "1 / 3 / 2 / 4" }} onClick={() => { setMapData({ ...mapData, heading: mapData.heading + 0.5 }) }} >⟳</Button>
				<Button className="btn-warning" style={{ gridArea: "1 / 1 / 2 / 2" }} onClick={() => { setMapData({ ...mapData, heading: mapData.heading - 0.5 }) }} >⟲</Button>

				<Button className={delta === 0.0001 ? "" : "btn-info"} style={{ gridArea: "2 / 2 / 3 / 3" }} onClick={() => { setDelta(delta === 0.0001 ? 0.00001 : 0.0001) }} >{delta === 0.0001 ? "Precise" : "Rough"}</Button>
				{/* <Button style={{gridArea: ""}} onClick={() => { setDelta(delta * 0.1) }} >- ({delta})</Button> */}

				<input type="file" style={{ gridArea: "4 / 1 / 5 / 6" }} onChange={onFileChange} />

				<Button style={{ gridArea: "5 / 1 / 6 / 6" }} onClick={() => { saveMapData() }} >Save</Button>
			</div>
			<p>Note: The map will be rotated the same amount as the image in the app.</p>
		</div>
	);
}

const containerStyle = {
	width: '100vw',
	height: '70vh',
};
